import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Plus, X, Trash2, User, Building2 } from "lucide-react";
import { SPOR_ALLE, KONTAKT_KATEGORIER, fmtDue, statusEn, statusTone, erAktivStage, PRI_LABEL, blokkSok, hentMedlemmer } from "./blokkData";
import CelleEditor from "./CelleEditor";

const SPOR_LABEL = Object.fromEntries(SPOR_ALLE);
const PRI_RANG = { P1: 0, P2: 1, P3: 2, M: 3 };
const KAT_ORDEN = ["styret", "byraa", "leverandor", "presse", "annet", "partnere", "speakers"];
const KAT_KEYS = new Set(KONTAKT_KATEGORIER.map(([k]) => k));

const DEFAULT_BREDDER = {
  navn: 250, katFirma: 165, pkat: 185, rolle: 155, ask: 250, route: 165,
  stage: 138, neste: 280, frist: 100, pri: 64, eier: 150,
};

const dueDenneUken = (frist) => {
  if (!frist) return false;
  const d = new Date(`${String(frist).slice(0, 10)}T12:00:00`);
  const naa = new Date();
  naa.setHours(12, 0, 0, 0);
  return (d - naa) / 86400000 <= 6;
};

const verdi = (r, k) => {
  if (k === "navn") return (r.navn || "").toLowerCase();
  if (k === "rolle") return (r.rolleType || "å").toLowerCase();
  if (k === "katFirma") return (r.kategoriFirma || "å").toLowerCase();
  if (k === "pkat") return (r.partnerKategori || "å").toLowerCase();
  if (k === "geo") return r.geo || "å";
  if (k === "land") return (r.land || "å").toLowerCase();
  if (k === "ask") return (r.ask || "å").toLowerCase();
  if (k === "route") return ((r.routeTypeLabel || "") + (r.route || "å")).toLowerCase();
  if (k === "eier") return (r.ansvarlig || "å").toLowerCase();
  if (k === "stage") return statusEn(r) || "";
  if (k === "neste") return (r.nesteSteg || "å").toLowerCase();
  if (k === "frist") return r.frist || "9999";
  return PRI_RANG[r.pri] ?? 9;
};

function NyTarget({ eier, onLegg }) {
  const [aapen, setAapen] = useState(false);
  const [sok, setSok] = useState("");
  const [treff, setTreff] = useState([]);
  const [jobber, setJobber] = useState(false);

  useEffect(() => {
    if (!aapen) return undefined;
    const t = setTimeout(() => blokkSok(sok, "speakers,partners").then(setTreff).catch(() => setTreff([])), 200);
    return () => clearTimeout(t);
  }, [sok, aapen]);

  const velg = async (valg) => {
    setJobber(true);
    await onLegg(eier.id, valg);
    setJobber(false);
    setSok("");
    setAapen(false);
  };

  if (!aapen) {
    return (
      <button type="button" className="dok-atab-ny" onClick={() => setAapen(true)} data-testid={`dok-atab-ny-${eier.id}`}>
        <Plus size={12} /> Legg til target{eier.id !== "uten" ? ` for ${eier.navn}` : ""}
      </button>
    );
  }
  const eksakt = treff.some((r) => r.navn.toLowerCase() === sok.trim().toLowerCase());
  return (
    <div className="dok-atab-ny-form" data-testid={`dok-atab-ny-form-${eier.id}`}>
      <div className="felt">
        <input value={sok} onChange={(e) => setSok(e.target.value)} autoFocus disabled={jobber}
          placeholder="Søk etter navn eller selskap — eller skriv nytt …"
          onKeyDown={(e) => e.key === "Escape" && setAapen(false)}
          className="dok-blokk-input" data-testid={`dok-atab-ny-input-${eier.id}`} />
        <button type="button" className="lukk" onClick={() => setAapen(false)} data-testid={`dok-atab-ny-lukk-${eier.id}`}><X size={12} /></button>
      </div>
      <div className="dok-blokk-treff">
        {treff.slice(0, 5).map((r) => (
          <button key={r.id} type="button" disabled={jobber} onClick={() => velg(r)} data-testid={`dok-atab-ny-treff-${r.id}`}>
            <Plus size={11} /><span className="truncate">{r.navn}</span>
            <span className="under truncate">{r.under || r.stageLabel}</span>
          </button>
        ))}
        {sok.trim().length > 1 && !eksakt && (
          <button type="button" className="opprett" disabled={jobber} onClick={() => velg({ nyttNavn: sok.trim() })}
            data-testid={`dok-atab-ny-opprett-${eier.id}`}>
            <Plus size={11} /> Opprett «{sok.trim()}» som ny target
          </button>
        )}
      </div>
    </div>
  );
}

function NyKontakt({ gruppe, onLegg }) {
  const [aapen, setAapen] = useState(false);
  const [navn, setNavn] = useState("");
  const [kategori, setKategori] = useState(KAT_KEYS.has(gruppe.id) ? gruppe.id : "annet");
  const [jobber, setJobber] = useState(false);

  if (!aapen) {
    return (
      <button type="button" className="dok-atab-ny" onClick={() => setAapen(true)} data-testid={`dok-atab-nykontakt-${gruppe.id}`}>
        <Plus size={12} /> Legg til kontakt{gruppe.mode === "owner" && gruppe.id !== "uten" ? ` for ${gruppe.navn}` : ""}
      </button>
    );
  }
  const send = async (e) => {
    e.preventDefault();
    if (!navn.trim()) return;
    setJobber(true);
    await onLegg(gruppe, { navn: navn.trim(), kategori });
    setJobber(false);
    setNavn("");
    setAapen(false);
  };
  return (
    <form className="dok-atab-ny-form" onSubmit={send} data-testid={`dok-atab-nykontakt-form-${gruppe.id}`}>
      <div className="felt">
        <input value={navn} onChange={(e) => setNavn(e.target.value)} autoFocus disabled={jobber}
          placeholder="Navn — person eller organisasjon"
          onKeyDown={(e) => e.key === "Escape" && setAapen(false)}
          className="dok-blokk-input" data-testid={`dok-atab-nykontakt-input-${gruppe.id}`} />
        {gruppe.mode === "owner" && (
          <select value={kategori} onChange={(e) => setKategori(e.target.value)}
            className="dok-blokk-input velg" data-testid={`dok-atab-nykontakt-kategori-${gruppe.id}`}>
            {KONTAKT_KATEGORIER.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
        <button type="submit" className="dok-atab-ny-lagre" disabled={jobber || !navn.trim()}
          data-testid={`dok-atab-nykontakt-lagre-${gruppe.id}`}>{jobber ? "…" : "Legg til"}</button>
        <button type="button" className="lukk" onClick={() => setAapen(false)} data-testid={`dok-atab-nykontakt-lukk-${gruppe.id}`}><X size={12} /></button>
      </div>
    </form>
  );
}

function TargetAvatar({ r, kilde }) {
  const org = kilde === "partners" || r.type === "partner" || r.targetType === "org";
  if (r.bilde) {
    return <i className={`dok-tavatar${org ? " org" : ""}`} data-testid={`dok-tavatar-${r.id}`}><img src={r.bilde} alt="" loading="lazy" /></i>;
  }
  return <i className={`dok-tavatar init${org ? " org" : ""}`} data-testid={`dok-tavatar-${r.id}`}>{(r.navn || "?")[0]}</i>;
}

function EierAvatar({ r }) {
  if (!r.ansvarlig) return <span className="dok-eier-chip uten"><i className="ut">!</i>Unassigned</span>;
  return (
    <span className="dok-eier-chip">
      {r.eierBilde
        ? <i className="foto"><img src={r.eierBilde} alt="" /></i>
        : <i style={{ background: r.eierFarge || "#8B5CF6" }}>{r.ansvarlig[0]}</i>}
      {r.ansvarlig}
    </span>
  );
}

const PriMark = ({ pri }) => (pri
  ? <em className={`dok-primark p-${pri.toLowerCase()}`} title={PRI_LABEL[pri] || pri}>{pri}</em>
  : null);

const Meta = ({ vis, tom, className = "", onClick, testid, children }) => {
  if (!vis && !onClick) return null;
  if (!onClick) return <span className={`dok-radmeta ${className}`}>{children}</span>;
  return (
    <button type="button" className={`dok-radmeta klikk ${vis ? "" : "tom"} ${className}`}
      onClick={onClick} data-testid={testid}>{vis ? children : tom}</button>
  );
};

function Seksjon({ gruppe, rader, naviger, sortKey, sortDir, onSort, editable, master, kilde, frys, bredder, onResize, onResetBredde, kanEdit, apneEdit, onNyTarget, onNyKontakt, onSlettRad, kollapset, onToggle }) {
  const sortert = useMemo(() => [...rader].sort((a, b) => {
    const va = verdi(a, sortKey);
    const vb = verdi(b, sortKey);
    const c = va < vb ? -1 : va > vb ? 1 : 0;
    return sortDir === "desc" ? -c : c;
  }), [rader, sortKey, sortDir]);
  const kritiske = rader.filter((r) => r.pri === "P1").length;
  const uken = rader.filter((r) => dueDenneUken(r.frist)).length;
  const dialog = rader.filter((r) => erAktivStage(r.stage)).length;
  const erKat = gruppe.mode === "kategori";
  const erFlat = gruppe.mode === "flat";
  const visEier = erKat || erFlat;
  const kols = [
    ["navn", "Target"],
    ...(kilde === "partners"
      ? [["pkat", "Partnership category"], ["rolle", "Partner role"]]
      : kilde === "speakers" ? [["rolle", "Role"]] : [["rolle", "Role / type"]]),
    ["ask", "Desired outcome"],
    ["stage", "Status"], ["neste", "Next move"],
    ...(visEier ? [["eier", "Owner"]] : []),
  ];
  const kanLeggeTil = master && !erFlat && (!erKat || KAT_KEYS.has(gruppe.id));
  const fk = (i) => (frys > i ? ` frys${i === frys - 1 ? " frys-siste" : ""}` : "");
  const fs = (i) => (frys > i && i > 0 ? { left: bredder.navn || 190 } : undefined);
  return (
    <div className="dok-atab-gruppe" data-testid={`dok-atab-eier-${gruppe.id}`}>
      {!erFlat && (
        <button type="button" className="dok-atab-hode" onClick={onToggle} data-testid={`dok-atab-kollaps-${gruppe.id}`}>
          {!erKat && (gruppe.id === "uten"
            ? <i className="ut">!</i>
            : <i style={{ background: gruppe.farge || "#8B5CF6" }}>{gruppe.navn[0]}</i>)}
          <b>{gruppe.navn}</b>
          <span className="antall">{rader.length} {erKat ? "targets" : "relationships"}</span>
          <span className="sum">{kritiske} critical · {uken} due this week · {dialog} active dialogues</span>
          <span className="pilboks">{kollapset ? <ChevronDown size={13} /> : <ChevronUp size={13} />}</span>
        </button>
      )}
      {!kollapset && (<>
      <div className="dok-atab-scroll">
        <table className={`dok-atab dok-atab-bred dok-atab-fast${frys >= 2 ? " dok-atab-frys2" : ""}`}>
          <thead>
            <tr>
              {kols.map(([k, l], i) => (
                <th key={k} className={`k-${k}${fk(i)}`} style={{ width: bredder[k] || 120, ...(fs(i) || {}) }}>
                  <button type="button" onClick={() => onSort(k)} data-testid={`dok-atab-sort-${gruppe.id}-${k}`}>
                    {l}
                    {sortKey === k && (sortDir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
                  </button>
                  <span className="dok-kol-resize" role="presentation"
                    onMouseDown={(e) => onResize(e, k)}
                    onDoubleClick={(e) => { e.stopPropagation(); onResetBredde(k); }}
                    data-testid={`dok-kol-resize-${gruppe.id}-${k}`} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortert.map((r) => {
              const d = fmtDue(r.frist);
              const rolleEdit = kanEdit && (r.type === "partner" || r.type === "speaker");
              const ruteTekst = [r.routeTypeLabel, (r.route && r.route.toLowerCase() !== (r.routeTypeLabel || "").toLowerCase()) ? r.route : null]
                .filter(Boolean).join(" · ");
              return (
                <tr key={`${r.type}-${r.id}`} onClick={() => naviger(r)} data-testid={`dok-atab-rad-${r.id}`}>
                  <td className={`navn${fk(0)}`}>
                    <span className="dok-navncelle">
                      <TargetAvatar r={r} kilde={kilde} />
                      <span className="dok-navnstakk">
                        <span className="dok-navnlinje">
                          {kilde === "speakers" && (
                            kanEdit ? (
                              <button type="button" className={`dok-type-ikon ${r.targetType === "org" ? "org" : "person"}`}
                                onClick={(e) => apneEdit(e, r, "targetType")}
                                title={r.targetType === "org" ? "Organisation target" : "Person"}
                                data-testid={`dok-atab-type-${r.id}`}>
                                {r.targetType === "org" ? <Building2 size={11} /> : <User size={11} />}
                              </button>
                            ) : (
                              <span className={`dok-type-ikon ${r.targetType === "org" ? "org" : "person"}`}>
                                {r.targetType === "org" ? <Building2 size={11} /> : <User size={11} />}
                              </span>
                            )
                          )}
                          {(r.pri || kanEdit) && (
                            kanEdit ? (
                              <button type="button" className={`dok-primark-knapp${r.pri ? "" : " tom"}`}
                                onClick={(e) => apneEdit(e, r, "pri")} title="Priority" data-testid={`dok-atab-pri-${r.id}`}>
                                {r.pri ? <PriMark pri={r.pri} /> : <em className="dok-primark tom">P</em>}
                              </button>
                            ) : <PriMark pri={r.pri} />
                          )}
                          <span className="dok-navn-tekst">{r.navn}</span>
                          {r.under && !r.navn.includes(r.under) && <span className="under"> — {r.under}</span>}
                        </span>
                        <Meta vis={!!ruteTekst} tom="+ Contact route"
                          onClick={kanEdit ? (e) => apneEdit(e, r, "route") : undefined}
                          testid={`dok-atab-route-${r.id}`}>{ruteTekst}</Meta>
                      </span>
                    </span>
                    {onSlettRad && editable && (
                      <button type="button" className="dok-rad-slett" title="Slett fra masterlisten"
                        onClick={(e) => { e.stopPropagation(); onSlettRad(r); }}
                        data-testid={`dok-rad-slett-${r.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                  {kilde === "partners" && (
                    <td className={`rolle${fk(1)}${kanEdit ? " kan-edit" : ""}`} style={fs(1)}
                      onClick={kanEdit ? (e) => apneEdit(e, r, "pkat") : undefined}>
                      {r.partnerKategori || "—"}
                      <Meta vis={!!r.land} tom="+ Country"
                        onClick={kanEdit ? (e) => apneEdit(e, r, "land") : undefined}
                        testid={`dok-atab-land-${r.id}`}>{r.land}</Meta>
                    </td>
                  )}
                  <td className={`rolle${kilde === "partners" ? "" : fk(1)}${rolleEdit ? " kan-edit" : ""}`}
                    style={kilde === "partners" ? undefined : fs(1)}
                    onClick={rolleEdit ? (e) => apneEdit(e, r, "rolle") : undefined}>
                    {r.rolleType || "—"}
                    {kilde === "speakers" && (
                      <Meta vis={!!r.geo} tom="+ Geo" className={r.geo ? `geo ${r.geo}` : ""}
                        onClick={rolleEdit ? (e) => apneEdit(e, r, "geo") : undefined}
                        testid={`dok-atab-geo-${r.id}`}>{r.geo === "norge" ? "Norge" : "Internasjonal"}</Meta>
                    )}
                  </td>
                  <td className={`ask${kanEdit ? " kan-edit" : ""}`} title={r.ask || ""}
                    onClick={kanEdit ? (e) => apneEdit(e, r, "ask") : undefined}>{r.ask || "—"}</td>
                  <td className={`status${kanEdit ? " kan-edit" : ""}`} onClick={kanEdit ? (e) => apneEdit(e, r, "stage") : undefined}>
                    <span className={`dok-status-chip t-${statusTone(r.stage)}`}>{statusEn(r)}</span>
                  </td>
                  <td className={`neste${kanEdit ? " kan-edit" : ""}`} title={r.nesteSteg || ""}
                    onClick={kanEdit ? (e) => apneEdit(e, r, "neste") : undefined}>
                    <span className="dok-neste-tekst">{r.nesteSteg || "—"}</span>
                    <Meta vis={!!d} tom="+ Due" className={d ? `due ${d.tone}` : ""}
                      onClick={kanEdit ? (e) => apneEdit(e, r, "frist") : undefined}
                      testid={`dok-atab-due-${r.id}`}>{d ? d.label : null}</Meta>
                  </td>
                  {visEier && (
                    <td className={`eier${kanEdit ? " kan-edit" : ""}`}
                      onClick={kanEdit ? (e) => apneEdit(e, r, "eier") : undefined}><EierAvatar r={r} /></td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editable && !master && <NyTarget eier={gruppe} onLegg={onNyTarget} />}
      {editable && kanLeggeTil && <NyKontakt gruppe={gruppe} onLegg={onNyKontakt} />}
      </>)}
    </div>
  );
}

export default function AssignmentTabell({ rader, naviger, editable, onNyTarget, onNyKontakt, onSlettRad = null, gruppering = "owner", master = false, kilde = "", frys = 1, refresh = null, kompakt = false }) {
  const [sortKey, setSortKey] = useState("pri");
  const [sortDir, setSortDir] = useState("asc");
  const [kollapset, setKollapset] = useState(() => new Set());
  const [edit, setEdit] = useState(null);
  const [medlemmer, setMedlemmer] = useState([]);
  const [bredder, setBredder] = useState(() => {
    try { return { ...DEFAULT_BREDDER, ...JSON.parse(localStorage.getItem(`nas_dok_kolbredde_${kilde || "std"}`) || "{}") }; }
    catch { return { ...DEFAULT_BREDDER }; }
  });
  const dragRef = useRef(null);
  const kanEdit = !!(editable && master && refresh);

  useEffect(() => {
    if (kanEdit) hentMedlemmer().then(setMedlemmer).catch(() => setMedlemmer([]));
  }, [kanEdit]);

  const lagreBredder = (b) => localStorage.setItem(`nas_dok_kolbredde_${kilde || "std"}`, JSON.stringify(b));
  const onResize = (e, k) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { k, startX: e.clientX, startB: bredder[k] || DEFAULT_BREDDER[k] || 120 };
    const flytt = (ev) => setBredder((prev) => ({ ...prev, [dragRef.current.k]: Math.max(56, dragRef.current.startB + ev.clientX - dragRef.current.startX) }));
    const slipp = () => {
      window.removeEventListener("mousemove", flytt);
      window.removeEventListener("mouseup", slipp);
      setBredder((prev) => { lagreBredder(prev); return prev; });
    };
    window.addEventListener("mousemove", flytt);
    window.addEventListener("mouseup", slipp);
  };
  const onResetBredde = (k) => setBredder((prev) => {
    const n = { ...prev, [k]: DEFAULT_BREDDER[k] || 120 };
    lagreBredder(n);
    return n;
  });
  const apneEdit = (e, rad, felt) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setEdit({ rad, felt, x: rect.left, y: rect.bottom + 4 });
  };
  const toggleGruppe = (id) => setKollapset((prev) => {
    const neste = new Set(prev);
    if (neste.has(id)) neste.delete(id); else neste.add(id);
    return neste;
  });
  const onSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };
  const grupper = useMemo(() => {
    if (gruppering === "flat") {
      return [{ id: "alle", navn: "", mode: "flat", rader }];
    }
    const m = new Map();
    rader.forEach((r) => {
      const erKat = gruppering === "kategori";
      const key = erKat ? (r.spor || "annet") : (r.eierId || "uten");
      const navn = erKat ? (r.sporLabel || SPOR_LABEL[r.spor] || "Annet") : (r.ansvarlig || "Unassigned");
      const g = m.get(key) || { id: key, navn, farge: r.eierFarge, mode: erKat ? "kategori" : "owner", rader: [] };
      g.rader.push(r);
      m.set(key, g);
    });
    const alle = [...m.values()];
    if (gruppering === "kategori") {
      alle.sort((a, b) => (KAT_ORDEN.includes(a.id) ? KAT_ORDEN.indexOf(a.id) : 50) - (KAT_ORDEN.includes(b.id) ? KAT_ORDEN.indexOf(b.id) : 50));
    } else {
      alle.sort((a, b) => (a.id === "uten") - (b.id === "uten") || b.rader.length - a.rader.length);
    }
    return alle;
  }, [rader, gruppering]);
  if (!rader.length) {
    return (
      <div>
        <p className="dok-blokk-tom">Ingen i dette utvalget.</p>
        {editable && !master && <NyTarget eier={{ id: "uten", navn: "Unassigned" }} onLegg={onNyTarget} />}
        {editable && master && <NyKontakt gruppe={{ id: "uten", navn: "Unassigned", mode: "owner" }} onLegg={onNyKontakt} />}
      </div>
    );
  }
  return (
    <div className={`dok-atab-wrap${kompakt ? " dok-kompakt" : ""}`} data-testid="dok-atab">
      {grupper.map((g) => (
        <Seksjon key={g.id} gruppe={g} rader={g.rader} naviger={naviger}
          sortKey={sortKey} sortDir={sortDir} onSort={onSort}
          editable={editable} master={master} kilde={kilde} frys={frys}
          bredder={bredder} onResize={onResize} onResetBredde={onResetBredde}
          kanEdit={kanEdit} apneEdit={apneEdit}
          onNyTarget={onNyTarget} onNyKontakt={onNyKontakt} onSlettRad={onSlettRad}
          kollapset={kollapset.has(g.id)} onToggle={() => toggleGruppe(g.id)} />
      ))}
      {edit && <CelleEditor edit={edit} medlemmer={medlemmer} onLukk={() => setEdit(null)} refresh={refresh} />}
    </div>
  );
}
